import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getConsentJsonHeaders } from "./consentHeaders";
import { getConsentPreferencesKey, type ConsentPreference } from "./useGetConsentPreferences";

type ConsentPreferenceWrite = {
    itemId: number;
    enabled: boolean;
};

type UpdateConsentPreferencesInput = {
    preferences: ConsentPreferenceWrite[];
};

type ConsentPreferencesHydraCache = {
    data?: {
        "hydra:member"?: ConsentPreference[];
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

type ConsentPreferencesCache = ConsentPreference[] | ConsentPreferencesHydraCache | undefined;

export const applyConsentPreferenceCacheUpdate = (
    currentPreferences: ConsentPreferencesCache,
    preferences: ConsentPreferenceWrite[],
    decidedAt: string,
): ConsentPreferencesCache => {
    if (currentPreferences === undefined) {
        return currentPreferences;
    }

    const preferencesByItemId = new Map(preferences.map((preference) => [preference.itemId, preference.enabled]));

    const updatePreferences = (preferenceItems: ConsentPreference[]) =>
        preferenceItems.map((preference) => {
            const enabled = preferencesByItemId.get(preference.itemId);

            if (enabled === undefined) {
                return preference;
            }

            return {
                ...preference,
                enabled,
                updatedAt: decidedAt,
            };
        });

    if (Array.isArray(currentPreferences)) {
        return updatePreferences(currentPreferences);
    }

    const hydraMembers = currentPreferences.data?.["hydra:member"];

    if (!Array.isArray(hydraMembers)) {
        return currentPreferences;
    }

    return {
        ...currentPreferences,
        data: {
            ...currentPreferences.data,
            "hydra:member": updatePreferences(hydraMembers),
        },
    };
};

export const useUpdateConsentPreferences = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: UpdateConsentPreferencesInput) => {
            const { preferences } = options;

            return loggedInApiCall.post(
                "/api/users/me/consent-preferences",
                { preferences },
                {
                    headers: getConsentJsonHeaders(),
                },
            );
        },
        onSuccess: async (_response, { preferences }) => {
            const decidedAt = new Date().toISOString();

            queryClient.setQueryData<ConsentPreferencesCache>([getConsentPreferencesKey], (currentPreferences) =>
                applyConsentPreferenceCacheUpdate(currentPreferences, preferences, decidedAt),
            );

            await queryClient.invalidateQueries([getConsentPreferencesKey]);
        },
    });
};

export type { ConsentPreferenceWrite };
